/**
 * <CheckpointReward> — le pétale qu'un checkpoint fait gagner (molecule).
 *
 * L'ancien `PetalRewardBadge`, dans les glyphes pleins du jeu. Tant que la conversation dure : une
 * barre qui avance à chaque réponse et le pétale au bout, à gagner. La barre s'approche du bout
 * sans jamais l'atteindre (`rewardProgress`, la courbe d'aujourd'hui : 35 %, 57 %, 70 %…) : on ne
 * sait pas combien de tours Sam posera. Gagné : la chip de gain, « +1 pétale ».
 *
 * Ni rebond, ni reflet qui passe : le gain se lit (le livre : « jamais de rebond »). Le son du gain,
 * celui de l'ancien (`petals-gained-2.mp3`), est joué par l'écran, comme tout son du design system.
 *
 * Composes DS pieces only — `<ProgressBar>`, `<GameGlyph>`, `<GainChip>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ProgressBar } from "../../atoms/ProgressBar"
import { GainChip } from "../GainChip"
import { GameGlyph } from "../GameGlyph"

export type CheckpointRewardProps = {
    /** Les réponses déjà données dans la conversation. */
    answered: number
    /** Le pétale est gagné : la conversation est allée au bout. */
    earned: boolean
    testID?: string
}

const TEXTS = {
    pending: "Un pétale à gagner",
}

/** La barre ne touche le bout qu'une fois le pétale gagné. */
const CEILING = 90
const PACE = 0.5
const BAR_WIDTH = 72
const BAR_HEIGHT = 6

/** Où en est la barre, en % : elle s'approche de 90 sans l'atteindre. */
function rewardProgress(answered: number): number {
    return CEILING * (1 - Math.exp(-Math.max(0, answered) * PACE))
}

export function CheckpointReward(props: CheckpointRewardProps) {
    const { answered, earned, testID } = props

    if (earned) return <GainChip amount={1} unit="petals" testID={testID} />

    return (
        <View style={styles.row} testID={testID} accessible accessibilityLabel={TEXTS.pending}>
            <View style={styles.bar}>
                <ProgressBar
                    value={rewardProgress(answered)}
                    color="role"
                    trackColor="border"
                    height={BAR_HEIGHT}
                    radius="full"
                />
            </View>
            <GameGlyph name="petals" size={24} plate={false} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["2"],
    },
    bar: {
        width: BAR_WIDTH,
    },
}))
